/**
 * AdminSessionContext — resolves whether the signed-in session may use the
 * admin control panel.
 *
 * Login itself is the ordinary `/api/v1/Auth/Login` flow in `AuthContext`; its
 * response carries no role and does not reliably carry `userGUID` (the API
 * schema `AuthResponse` omits it), so the role is read back from the admin API
 * with the session's own user GUID. The GUID is resolved from `GetMyProfile`
 * when neither the auth state nor storage has it — the member apps get it from
 * `ProfileContext`, but the admin panel does not mount that provider, so this
 * context resolves it independently.
 *
 * Requires `i18n/registerAdmin` to have been imported by the host app; without
 * it the `session.*` failure copy falls back to the raw key.
 *
 * The role check is server-enforced: the endpoint answers 401 for a non-admin
 * bearer, which is exactly the answer this context needs. Client-side gating
 * here is for navigation only — every admin endpoint enforces the role again
 * on the server.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import adminService from '../api/admin/adminService';
import { ADMIN_PANEL_ROLES, type AdminUser } from '../api/admin/adminTypes';
import userProfileApi from '../api/userProfile/userProfileApi';
import { API_ENDPOINTS } from '../api/endpoints';
import { STORAGE_KEYS } from '../constants';
import { storage } from '../platform/storage';
import {
  getUserFacingErrorMessage,
  UnusableResponseError,
} from '../utils/apiErrorHandler';
import { useTranslation } from '../i18n/useTranslation';
import { useAuth } from './AuthContext';

export interface AdminSessionContextValue {
  /** True while the session is bootstrapping or the role is being resolved. */
  initializing: boolean;
  /** True once a signed-in session was confirmed to hold an admin role. */
  isAdmin: boolean;
  adminUser: AdminUser | null;
  /** Set when the role could not be resolved (network / server failure). */
  error: string | null;
  /** True when the session is valid but the account holds no admin role. */
  accessDenied: boolean;
  recheck: () => void;
}

const adminSessionContext = createContext<AdminSessionContextValue | null>(null);

/**
 * `idle` covers both "not started" and "signed out". Keeping it distinct from
 * `settled` matters: treating an unstarted check as finished would report
 * "not an admin" for one commit and flash the access-denied screen.
 */
type ResolutionStatus = 'idle' | 'resolving' | 'settled';

const isAuthorizationFailure = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
};

export const AdminSessionProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const { initializing: authInitializing, isAuthenticated, userGUID } = useAuth();
  const { t } = useTranslation('admin');
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [status, setStatus] = useState<ResolutionStatus>('idle');
  const [recheckToken, setRecheckToken] = useState(0);
  const requestRef = useRef(0);

  // Held in a ref so re-running the whole role resolution is not the price of
  // an i18n identity change; only the failure copy needs the latest `t`.
  const roleCheckFailedRef = useRef('');
  roleCheckFailedRef.current = t('session.roleCheckFailed');

  const recheck = useCallback(() => {
    setRecheckToken(token => token + 1);
  }, []);

  useEffect(() => {
    if (authInitializing) {
      return;
    }
    if (!isAuthenticated) {
      // Invalidate any in-flight resolution: without this a response that
      // lands after sign-out still matches its request id and would restore
      // the previous operator's session.
      requestRef.current += 1;
      setAdminUser(null);
      setAccessDenied(false);
      setError(null);
      setStatus('idle');
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setStatus('resolving');
    setError(null);
    setAccessDenied(false);

    // The login response does not include userGUID (the API schema omits it).
    // The member apps resolve it from ProfileContext → GetMyProfile, but the
    // admin panel does not mount that provider, so this context resolves it
    // itself: if the auth state already carries it (session restore from
    // storage), use it directly; otherwise fetch GetMyProfile once, persist
    // the GUID so future boots skip the extra call, then proceed.
    const resolveGuid = async (): Promise<string> => {
      if (userGUID) {
        return userGUID;
      }
      const stored = await storage.getItem<string>(STORAGE_KEYS.USER_GUID);
      if (stored) {
        return stored;
      }
      const { data } = await userProfileApi.getProfile();
      const guid = data?.userGuid;
      if (!guid) {
        // Not a plain Error: `getUserFacingErrorMessage` keeps a non-technical
        // Error.message, which would put untranslated English on screen in
        // place of `session.roleCheckFailed`.
        throw new UnusableResponseError(API_ENDPOINTS.USER_PROFILE.GET_MY_PROFILE);
      }
      await storage.setItem(STORAGE_KEYS.USER_GUID, guid);
      return guid;
    };

    resolveGuid()
      .then(guid => adminService.getUser(guid))
      .then(user => {
        if (requestRef.current !== requestId) {
          return;
        }
        const allowed = user.role != null && ADMIN_PANEL_ROLES.includes(user.role);
        setAdminUser(allowed ? user : null);
        setAccessDenied(!allowed);
      })
      .catch((cause: unknown) => {
        if (requestRef.current !== requestId) {
          return;
        }
        setAdminUser(null);
        if (isAuthorizationFailure(cause)) {
          setAccessDenied(true);
          return;
        }
        setError(
          getUserFacingErrorMessage(cause, roleCheckFailedRef.current),
        );
      })
      .finally(() => {
        if (requestRef.current === requestId) {
          setStatus('settled');
        }
      });

    return () => {
      requestRef.current += 1;
    };
  }, [authInitializing, isAuthenticated, userGUID, recheckToken]);

  const value = useMemo<AdminSessionContextValue>(
    () => ({
      // A signed-in session counts as initializing until its role is settled,
      // so consumers never read the pre-check state as "not an admin".
      initializing: authInitializing || (isAuthenticated && status !== 'settled'),
      isAdmin: adminUser != null,
      adminUser,
      error,
      accessDenied,
      recheck,
    }),
    [
      accessDenied,
      adminUser,
      authInitializing,
      error,
      isAuthenticated,
      recheck,
      status,
    ],
  );

  return (
    <adminSessionContext.Provider value={value}>
      {children}
    </adminSessionContext.Provider>
  );
};

export const useAdminSession = (): AdminSessionContextValue => {
  const session = useContext(adminSessionContext);
  if (!session) {
    throw new Error('useAdminSession must be used inside <AdminSessionProvider>');
  }
  return session;
};

export default adminSessionContext;
